import { useEffect, useRef, useState } from 'react'
import { Pencil } from 'lucide-react'
import { FieldRenderer } from './FieldRenderer'
import type { FieldDefinition } from '../types'

const PRESENTATIONAL_TYPES = new Set(['SEPARATOR', 'SPACER', 'HEADER', 'INSTRUCTION'])

type Props = {
  field: FieldDefinition
  value: unknown
  editable: boolean
  onChange: (value: unknown) => void
  contractId: number
  verificationBadge?: React.ReactNode
}

/**
 * Kompaktowy wiersz pola w sidebarze. Reużywa {@link FieldRenderer} (label + ptaszek + wartość) -
 * NIE renderuje własnego labela (unika duplikacji). Pełna funkcjonalność pola sekcji.
 *
 * - nieedytowalne (brak uprawnień): tylko READ;
 * - edytowalne, tryb odczytu: READ + hover-pencil do wejścia w edycję (osobny przycisk,
 *   żeby nie kolidować z klikalnym ptaszkiem weryfikacji);
 * - edytowalne, tryb edycji: pełny input, collapse do odczytu po utracie focusu.
 */
export function SidebarField({ field, value, editable, onChange, contractId, verificationBadge }: Props) {
  const [editing, setEditing] = useState(false)
  const editRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!editing) return
    const el = editRef.current?.querySelector<HTMLElement>('input, textarea, select, [tabindex]')
    el?.focus()
  }, [editing])

  // Typy prezentacyjne: nigdy edytowalne, FieldRenderer sam je obsługuje.
  if (PRESENTATIONAL_TYPES.has(field.fieldType)) {
    return <FieldRenderer field={field} value={value} mode="READ" contractId={contractId} />
  }

  if (editable && editing) {
    return (
      <div
        ref={editRef}
        onBlur={e => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setEditing(false)
        }}
      >
        <FieldRenderer
          field={field}
          value={value}
          mode="EDIT"
          onChange={onChange}
          contractId={contractId}
          verificationBadge={verificationBadge}
        />
      </div>
    )
  }

  return (
    <div className="group relative rounded hover:bg-slate-50/70 transition-colors">
      <FieldRenderer
        field={field}
        value={value}
        mode="READ"
        contractId={contractId}
        verificationBadge={verificationBadge}
      />
      {editable && (
        <button
          type="button"
          onClick={() => setEditing(true)}
          aria-label={`Edytuj: ${field.labelPl}`}
          className="absolute top-0 right-0 p-1 text-slate-300 opacity-0 group-hover:opacity-100 hover:text-slate-600 transition"
        >
          <Pencil size={12} aria-hidden />
        </button>
      )}
    </div>
  )
}
